"use client";

import React from "react";
import Link from "next/link";
import { useLocalization } from "@/providers/useLocalization";
import { useTranslations } from "@/providers/TranslationsProvider";

/** Matches the form's root id, so the closing call to action can scroll back up to it. */
export const APPLICATION_ANCHOR = "application";

const ICONS: Record<string, React.ReactNode> = {
  languages: <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.05 9.5A18 18 0 016.4 9m6.1 9h7M11 21l5-10 5 10M12.75 5C11.8 10.8 8 15.3 3 18" />,
  video: <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />,
  calendar: <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
  shield: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.6-4.02A12 12 0 0112 2.94a12 12 0 01-8.6 3.04A12 12 0 003 9c0 5.6 3.82 10.3 9 11.62C17.18 19.3 21 14.6 21 9c0-1.04-.13-2.05-.38-3.02z" />,
};

const BENEFITS = ["languages", "video", "calendar", "shield"] as const;
const STEPS = ["apply", "verify", "setup", "meet"] as const;
const FAQ = ["who", "licence", "documents", "edit", "time"] as const;

const SECTION_TITLE = "text-[20px] sm:text-[24px] font-semibold tracking-tight text-[var(--foreground)]";
const BODY = "text-[14px] sm:text-[15px] leading-[1.5] text-[var(--foreground-secondary)]";

/**
 * The /join page's landing content under the application form: why join,
 * how it works, common questions and a way back up to the form. Everything
 * it says is what the product does today (the review time is the same
 * promise the review step makes); nothing about fees or payouts.
 * `showApply` is off while the application is under review: the form is
 * then a status card, with nothing to go back up to. With `signInUrl` (a
 * visitor who isn't signed in) the call to action signs them in instead.
 */
export default function JoinLanding({ showApply = true, signInUrl }: Readonly<{ showApply?: boolean; signInUrl?: string }>) {
  const { t } = useTranslations();
  const { language } = useLocalization();
  const k = (key: string, fallback: string) => t(`join.landing.${key}`, fallback);

  const benefit: Record<(typeof BENEFITS)[number], [string, string]> = {
    languages: [k("benefit languages title", "Patients in their own language"), k("benefit languages body", "{{app}} works in English, Persian, German, French, Sorani and Kurmanji. Patients find you by the languages you hold sessions in.")],
    video: [k("benefit video title", "Online video sessions"), k("benefit video body", "Sessions run by video, right in the browser. You and your patient each get your own link to join.")],
    calendar: [k("benefit calendar title", "Your hours, your prices"), k("benefit calendar body", "Set your availability, session lengths and prices in your dashboard. Patients only see times you've opened.")],
    shield: [k("benefit shield title", "Verified, and private"), k("benefit shield body", "Every doctor is verified before being listed, and patients see the licence type and number, so they can trust who they book. Your documents stay with our review team.")],
  };
  const step: Record<(typeof STEPS)[number], [string, string]> = {
    apply: [k("step apply title", "Apply"), k("step apply body", "Tell us about yourself, your specialties and your licence.")],
    verify: [k("step verify title", "We verify your licence"), k("step verify body", "Usually within two working days. You can follow the status on this page.")],
    setup: [k("step setup title", "Set up your profile"), k("step setup body", "Add your bio, availability and session prices in the dashboard.")],
    meet: [k("step meet title", "Meet your patients"), k("step meet body", "Patients book and pay online, then join the video session with you.")],
  };
  const faq: Record<(typeof FAQ)[number], [string, string]> = {
    who: [k("faq who q", "Who can apply?"), k("faq who a", "Any doctor or psychologist with a valid licence, wherever they practise. In the form, choose the country that issued your licence; for a country without its own licence types, you just enter the issuing body.")],
    licence: [k("faq licence q", "Do patients see my licence?"), k("faq licence a", "Once you're approved, patients see your licence type and number, so they know you're verified. Your documents are only seen by our review team. Patients also see your public profile: your name, photo, specialties, languages, bio and what else you choose to add.")],
    documents: [k("faq documents q", "What do I need to apply?"), k("faq documents a", "Your licence number and the details of the body that issued it. A scan or photo of your licence is optional, but it speeds up verification.")],
    edit: [k("faq edit q", "Can I change my application?"), k("faq edit a", "Yes, until you submit it — your progress is saved as you go. If we can't approve it, we tell you why, and you can correct it and submit again.")],
    time: [k("faq time q", "How long does the review take?"), k("faq time a", "Usually up to two working days. You can follow the status on this page.")],
  };

  return (
    <div className="mt-16 sm:mt-24 flex flex-col gap-16 sm:gap-20">
      <section aria-labelledby="join-why">
        <h2 id="join-why" className={`${SECTION_TITLE} text-center`}>
          {k("why title", "Why doctors join {{app}}")}
        </h2>
        <ul className="mt-6 sm:mt-8 grid gap-3 sm:gap-4 sm:grid-cols-2">
          {BENEFITS.map((id) => (
            <li key={id} className="rounded-[1.5rem] bg-white dark:bg-[#1C1C1E] ring-1 ring-black/[0.05] dark:ring-white/[0.06] p-5 sm:p-6">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[#0071E3]/10 text-[#0071E3]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
                  {ICONS[id]}
                </svg>
              </span>
              <h3 className="mt-4 text-[16px] font-semibold tracking-tight text-[var(--foreground)]">{benefit[id][0]}</h3>
              <p className={`mt-1.5 ${BODY}`}>{benefit[id][1]}</p>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="join-how">
        <h2 id="join-how" className={`${SECTION_TITLE} text-center`}>
          {k("how title", "How it works")}
        </h2>
        <ol className="mt-6 sm:mt-8 flex flex-col gap-5">
          {STEPS.map((id, i) => (
            <li key={id} className="flex gap-4">
              <span className="shrink-0 w-8 h-8 rounded-full bg-[#0071E3] text-white text-[14px] font-semibold flex items-center justify-center" aria-hidden>
                {(i + 1).toLocaleString(language)}
              </span>
              <div className="pt-1">
                <h3 className="text-[16px] font-semibold tracking-tight text-[var(--foreground)]">{step[id][0]}</h3>
                <p className={`mt-1 ${BODY}`}>{step[id][1]}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="join-faq">
        <h2 id="join-faq" className={`${SECTION_TITLE} text-center`}>
          {k("faq title", "Questions")}
        </h2>
        {/* <details>, not Accordion: questions wrap instead of truncating, and
            the answers are in the server-rendered page. */}
        <div className="mt-6 sm:mt-8 flex flex-col gap-2">
          {FAQ.map((id) => (
            <details key={id} className="group rounded-2xl bg-black/[0.04] dark:bg-white/[0.06]">
              <summary className="list-none [&::-webkit-details-marker]:hidden min-h-12 px-4 py-3 flex items-center gap-3 cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC]/40">
                <span className="flex-1 text-start text-[15px] font-semibold tracking-tight text-[var(--foreground)]">{faq[id][0]}</span>
                <svg className="w-3.5 h-3.5 shrink-0 text-[var(--foreground-tertiary)] transition-transform duration-300 group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className={`px-4 pb-4 ${BODY}`}>{faq[id][1]}</p>
            </details>
          ))}
        </div>
      </section>

      {showApply && (
        <section className="rounded-[2rem] bg-[#0071E3]/[0.06] dark:bg-[#0A84FF]/[0.12] px-6 py-8 sm:py-10 text-center">
          <h2 className={SECTION_TITLE}>{k("cta title", "Ready to start?")}</h2>
          <p className={`mt-2 ${BODY}`}>{k("cta body", "Your progress is saved as you go, so you can finish your application later.")}</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={signInUrl ?? `#${APPLICATION_ANCHOR}`}
              className="inline-flex items-center justify-center h-12 px-7 rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white text-[16px] font-medium"
            >
              {signInUrl ? k("sign in to apply", "Sign in to apply") : k("cta apply", "Continue your application")}
            </a>
            <Link href={`/${language}/contact`} className="text-[15px] text-[#0066CC] dark:text-[#2997FF] hover:underline">
              {k("cta contact", "Have a question? Contact us")}
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
